<template>
  <div class="shortcuts text-dark">
    <div class="shortcuts__buttons">
      <label class="marg-10-right">{{ $t('vue2.substitute_hours.shortcuts') }}:</label>
      <button class="do-bttn" @click="getAbsentUsers">{{ $t('vue2.substitute_hours.show_all_absent') }}</button>
      <DDropdown :isShowDots="false" :isBottomStart="true" :propOffset="[5, 5]" v-if="isShowExternalShortCuts">
        <button slot="title" class="do-bttn dropdown-button">Rapporter <span class="icon-arrow-down5"></span></button>
        <ul slot="list">
          <li
            class="li-links__link"
            v-for="schoolLink in schoolLinks"
            :key="schoolLink.link">
            <a :href="schoolLink.link" target="_blank">{{ schoolLink.title }}
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                   stroke-width="1.5" stroke="currentColor" class="tw-icon w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
              </svg>
            </a>
          </li>
        </ul>
      </DDropdown>
    </div>
    <div class="shortcuts__overlay__bg" v-if="isShowAbsentUsers" @click="close"></div>
    <transition name="slideUp">
      <div class="shortcuts__overlay text-dark" id="printList" v-if="isShowAbsentUsers">
        <div class="shortcuts__overlay__header">
          <h2 class="text-dark"><span v-if="isLoading">{{ $t('vue2.substitute_hours.getting_all') }}</span><span
            v-else>{{ absentUsers.length }}</span>
            {{ $t('vue2.substitute_hours.absent_in_the_period') }} {{ startDateFormatted }} - {{ endDateFormatted }}
          </h2>
          <div class="shortcuts__overlay__header__buttons">
            <button @click="print" class="no-print"><span class="icon-printer3"></span></button>
            <div class="shortcuts__overlay__close icon__bg-circle no-print" @click="close">
              <span class="icon-cross2"></span>
            </div>
          </div>
        </div>
        <Loader v-if="isLoading"></Loader>
        <div class="shortcuts__users" v-else>
          <div class="filters__for__reasons no-print">
            <DoFiltersFilter
              :title="i18n['filter_reasons']"
              :tags="absenceEventsTypes"
              :selectedTags="selectedReasonsToFilter"
              @select="toggleFilter"
              @clear="selectedReasonsToFilter = []">
            </DoFiltersFilter>
          </div>
          <h4 v-if="absenceEvents.length < 1" class="italic">{{
              $t('vue2.substitute_hours.there_is_no_one_absent')
            }}</h4>
          <ul>
            <li class="shortcuts-user text-dark" v-for="absentUser in absentUsers" :key="absentUser.userCalendarId">
              <div class="shortcuts-user__header">
                <div class="shortcuts-user__header__name">{{ getUserNameFromCalId(absentUser.userCalendarId) }}</div>
                <div><label>{{ $t('vue2.substitute_hours.days_with_absence') }}:</label>
                  {{ getNumberOfDaysAbsent(absentUser.events) }}
                </div>
              </div>
              <ul class="text-dark">
                <li v-for="day in sortedEvents(absentUser.events)">
                  <ul class="events__list">
                    <li class="events__list__event" v-for="event in day">
                      <div class="events__list__event__meta">{{ event.start | ddd | capitalize }} {{ event.start | LL }}
                        kl. {{ event.start | LT }} -
                        {{ event.end | LT }},
                        <b>{{ event.title }}</b>
                      </div>
                      <div class="events__list__event__note"
                           v-if="event.teacher_notes && hasNoteWithoutHTML(event.teacher_notes).length > 0">
                        <span class="icon-pencil"></span>
                        <div v-html="event.teacher_notes"></div>
                      </div>
                      <p>{{ getDuration(event) }}</p>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import docendo from '../../docendo'
import Loader from '../common/Loader'
import moment from 'moment'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import DDropdown from '../common/DDropdown.vue'

const I18N = {
  da: {
    filter_reasons: 'Årsager'
  },
  en: {
    filter_reasons: 'Reasons'
  },
  nb: {
    filter_reasons: 'Årsaker'
  }
}

export default {
  components: {DDropdown, DoFiltersFilter, Loader},
  props: {
    start: {
      type: Object,
      default: moment()
    },
    end: {
      type: Object,
      default: moment()
    }
  },
  data () {
    return {
      isShowAbsentUsers: false,
      absenceEvents: [],
      isLoading: false,
      selectedReasonsToFilter: []
    }
  },
  computed: {
    i18n () {
      return I18N[this.locale]
    },
    user () {
      return this.$store.state.user
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    userIsSchoolAdmin () {
      return this.userHasPermission('can_manage_school')
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isShowExternalShortCuts () {
      return this.schoolCountry.toLowerCase() === 'dk' && this.userIsSchoolAdmin
    },
    schoolLinks () {
      return [
        {
          link: window.location.origin + '/app#/dashboard/' + this.schoolId,
          title: 'Antal vikartimer pr. klasse for skoleåret'
        },
        {
          link: window.location.origin + '/app#/dashboard/absence/' + this.schoolId,
          title: 'Personalets fraværstyper fordelt over måneder'
        },
        {
          link: window.location.origin + '/app#/dashboard/absence/subs/' + this.schoolId,
          title: 'Vikartimer ift. Fraværstyper'
        }
      ]
    },
    startDateFormatted () {
      return moment(this.start).locale(this.locale).format('DD. MMMM YYYY')
    },
    endDateFormatted () {
      return moment(this.end).locale(this.locale).format('DD. MMMM YYYY')
    },
    absenceEventsTypes () {
      const types = []
      this.absenceEvents.forEach(event => {
        const idx = types.findIndex(type => type === event.title)
        if (idx < 0) {
          types.push(event.title)
        }
      })
      return types
    },
    locale () {
      return this.$store.getters.locale
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    schoolUsers () {
      return this.$store.getters.usersSortedOnName
    },
    schoolUsersIds () {
      return this.schoolUsers.map(user => user.id)
    },
    schoolUserCalIds () {
      return this.schoolUsers.map(user => user.user_calendar_id)
    },
    absenceEventsFiltered () {
      if (!this.selectedReasonsToFilter.length) {
        return this.absenceEvents // Hvis intet er valgt, returnér alle events
      }
      return this.absenceEvents.filter(event => this.selectedReasonsToFilter.includes(event.title))
    },
    absentUsers () {
      let users = []
      this.absenceEventsFiltered.forEach(event => {
        let idx = users.findIndex(user => user.userCalendarId === event.calendar_id)
        if (idx < 0) {
          let user = {userCalendarId: event.calendar_id, events: [event]}
          users.push(user)
        } else {
          users[idx].events.push(event)
        }
      })
      return users
    }
  },
  methods: {
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      } else {
        return true
      }
    },
    toggleFilter (filter) {
      const idx = this.selectedReasonsToFilter.indexOf(filter)
      if (idx < 0) {
        this.selectedReasonsToFilter.push(filter)
      } else {
        this.selectedReasonsToFilter.splice(idx, 1)
      }
    },
    print () {
      // https://stackoverflow.com/questions/52343006/how-to-print-a-part-of-a-vue-component-without-losing-the-style
      const prtHtml = document.getElementById('printList').innerHTML
      // Get all stylesheets HTML
      let stylesHtml = ''
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
        stylesHtml += node.outerHTML
      }
      // Open the print window
      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')

      WinPrint.document.write(`<!DOCTYPE html><html><head>${stylesHtml}</head><body>${prtHtml}</body></html>`)
      setTimeout(_ => {
        WinPrint.document.close()
        WinPrint.focus()
        WinPrint.print()
        WinPrint.close()
      }, 1000)
    },
    hasNoteWithoutHTML (myString) {
      return myString.replace(/<[^>]*>?/gm, '')
    },
    open () {
      let el = document.querySelector('body')
      if (!el.classList.contains('no-scroll')) {
        el.classList.add('no-scroll')
      }
    },
    close () {
      this.isShowAbsentUsers = false
      document.querySelector('body').classList.remove('no-scroll')
    },
    getAbsentUsers () {
      this.open()
      this.isShowAbsentUsers = true
      this.absenceEvents = []
      this.isLoading = true
      docendo.calendarEvents2(this.schoolUserCalIds, this.start, this.end, ['absence'], data => {
        this.absenceEvents = data.length > 0 ? JSON.parse(data) : []
        this.isLoading = false
      }, failure => {
        this.isLoading = false
      })
    },
    sortedEvents (events) {
      let sortedEvent = events.sort((a, b) => {
        return moment(a.start).unix() - moment(b.start).unix()
      })
      return this.eventsSortedInDays(sortedEvent)
    },
    eventsSortedInDays (events) {
      return events.reduce((acc, curr, index) => {
        var val = moment(curr['start']).format('YYYY-MM-DD')
        acc[val] = acc[val] || []
        acc[val].push(curr)
        return acc
      }, {})
    },
    getUserNameFromCalId (calId) {
      return this.allUsersInCalIdHash[calId] && this.allUsersInCalIdHash[calId].name ? this.allUsersInCalIdHash[calId].name : 'User can not be found'
    },
    getDuration (event) {
      return this.humanizeMinutes(event.duration)
    },
    humanizeMinutes (data) {
      const minutes = data % 60
      const hours = (data - minutes) / 60
      const multiple = hours === 1 ? this.$i18n.t('vue2.substitute_hours.hour') : this.$i18n.t('vue2.substitute_hours.hours')
      return hours + ' ' + multiple + ' ' + minutes + ' ' + this.$t('vue2.substitute_hours.min')
    },
    getNumberOfDaysAbsent (userEvents) {
      if (!userEvents) {
        return 0
      }
      return Object.keys(this.sortedEvents(userEvents)).length
    }
  }
}
</script>

<style>
.shortcuts__overlay {
  position: fixed;
  transition: transform .4s, -webkit-transform .4s;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80vh;
  background-color: #edf0f5;
  overflow-y: auto;
  z-index: 9999999;
  box-shadow: 0 10px 40px -10px rgba(0, 64, 128, .2);
}

.shortcuts__overlay__bg {
  position: fixed;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
  background: rgba(0, 0, 0, .1);
  z-index: 99999;
}

.shortcuts__overlay__header {
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #d8d8d8;
}

.shortcuts__overlay__header__buttons {
  display: flex;
  align-items: center;
}

.shortcuts__overlay__close {
  font-size: 20px !important;
  margin-left: 4rem;
}

.slideUp-enter-active {
  transition: transform .3s;
}

.slideUp-enter, .slideUp-leave-to {
  transform: translateY(100%);
}

.shortcuts__users {
  padding: 20px;
}

.shortcuts-user {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #ffffff;
  margin-bottom: 20px;
  border: 1px solid #d8d8d8;
}

.shortcuts-user .shortcuts-user__header {
  border-bottom: 1px solid #d8d8d8;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
}

.shortcuts-user .shortcuts-user__header__name {
  font-size: 1.3rem;
  font-weight: bold;
}

.events__list > li {
  padding: 5px 20px;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
}

.events__list > li:nth-child(odd) {
  background-color: #f8fafb;
}

.events__list__event > * {
  flex: 1;
}

.events__list__event p {
  text-align: right;
}

.events__list__event__note {
  display: flex;
  align-items: center;
}

.events__list__event__note > span {
  margin-right: 5px;
}

.events__list__event__note div {
  display: flex;
  align-items: center;
}

.filters__for__reasons {
  margin-bottom: 1rem;
}

.shortcuts__buttons {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.li-links__link a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #222 !important;
  gap: 2rem;
}

.dropdown-button {
  font-size: 12px !important;
}

</style>



// WEBPACK FOOTER //
// HoursShowShortcuts.vue?b7e5dc10